import { ShareSeatImage } from '../components/ShareSeatImage';
import { SeatContributions } from '../components/SeatContributions';
import { rowPath, seatLayoutFor } from '@anjabom/core';
import { SeatFactsCard } from '../components/SeatFactsCard';
import { OfficialLayoutStatus } from '../components/OfficialLayoutStatus';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { buildFitEvidence, candidateId as createCandidateId, officialLayoutAssessment, priceDistribution, reviewSeat, sameSeat, seatLayouts, venueById } from '@anjabom/core';
import { Breadcrumb } from '../components/Breadcrumb';
import { EvidenceDistanceBadge } from '../components/EvidenceDistanceBadge';
import { ExternalLink } from '../components/ExternalLink';
import { track } from '../lib/analytics';
import { loadSeatEvidence, type SeatEvidence } from '../lib/compare-evidence';
import { buildCandidateEvidenceSnapshot } from '../lib/candidate-evidence';
import { ShareSeatButton } from '../components/ShareSeatButton';
import { markCompareCompleted, readSurfaceState } from '../lib/surface-state';
import { SurfaceBanner } from '../components/SurfaceBanner';
import { useLocalState } from '../state/LocalStateProvider';
import { parseSharedSeatKey } from '../lib/seat-share';
import { useUnknownDwellAnalytics } from '../lib/useUnknownDwellAnalytics';
import { SeatPlate } from '../components/SeatPlate';
import { SeatMemoryList } from '../components/SeatMemoryList';

export function SeatPage() {
  const { candidateId } = useParams();
  const navigate = useNavigate();
  const { state, addCandidate } = useLocalState();
  const candidate = useMemo(() => {
    const stored = state.candidates.find((item) => item.candidateId === candidateId);
    if (stored) return stored;
    const memory = state.reviews.map((review) => reviewSeat(review, state)).find((seat) => seat?.candidateId === candidateId);
    if (memory) return memory;
    const shared = parseSharedSeatKey(candidateId);
    const sharedVenue = shared ? venueById(shared.venueId) : undefined;
    const sharedLayout = shared ? seatLayouts.find((item) => item.venueId === shared.venueId && item.layoutVersionId === shared.layoutVersionId) : undefined;
    const sharedSpace = sharedVenue?.spaces.find((item) => item.id === sharedLayout?.spaceId);
    return shared && sharedVenue && sharedLayout && sharedSpace ? {
      candidateId: candidateId as string,
      categoryId: sharedVenue.categoryId,
      venueId: sharedVenue.id,
      venueName: sharedVenue.name,
      spaceId: sharedSpace.id,
      spaceName: sharedSpace.name,
      layoutVersionId: shared.layoutVersionId,
      instanceId: null,
      instanceLabel: null,
      sectionCode: shared.sectionCode,
      rowLabel: shared.rowLabel,
      seatNumber: shared.seatNumber,
      expectedPrice: null,
    } : undefined;
  }, [candidateId, state]);
  const stored = state.candidates.some((item) => item.candidateId === candidateId);
  const [server,setServer]=useState<SeatEvidence | null>(null);
  const [evidenceStatus, setEvidenceStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [evidenceCandidateId, setEvidenceCandidateId] = useState<string | null>(null);
  const [evidenceRetry, setEvidenceRetry] = useState(0);
  const [surface] = useState(readSurfaceState);
  const [candidateMessage, setCandidateMessage] = useState('');
  // biome-ignore lint/correctness/useExhaustiveDependencies: the retry counter deliberately restarts the same seat lookup.
  useEffect(() => {
    if (!candidate) return;
    const controller = new AbortController();
    setEvidenceCandidateId(candidate.candidateId);
    setServer(null);
    setEvidenceStatus('loading');
    void loadSeatEvidence(candidate, controller.signal).then((value) => {
      if (controller.signal.aborted) return;
      setServer(value);
      setEvidenceStatus('success');
      markCompareCompleted();
      track('compare_viewed');
    }).catch((error: unknown) => {
      if (controller.signal.aborted || (error instanceof Error && error.name === 'AbortError')) return;
      setEvidenceStatus('error');
    });
    return () => controller.abort();
  }, [candidate, evidenceRetry]);
  const reviews = candidate ? state.reviews.filter((review) => review.candidateId === candidate.candidateId) : [];
  const memories = candidate ? state.reviews.filter((review) => {
    const seat = reviewSeat(review, state);
    return seat && sameSeat(seat, candidate);
  }) : [];
  const memorySection = memories.length ? <section className="stack" aria-labelledby="same-seat-memory"><h2 id="same-seat-memory">이 자리에 앉았을 때의 기억이에요.</h2><p className="micro">지난 관람의 개인 경험이에요. 회차와 무대가 달라질 수 있어요.</p><SeatMemoryList reviews={memories} state={state} /></section> : null;
  const currentEvidenceStatus = candidate?.candidateId === evidenceCandidateId ? evidenceStatus : 'loading';
  const unknownEvidenceVisible = Boolean(currentEvidenceStatus === 'success' && candidate && server && server.evidence.length === 0 && reviews.length === 0);
  useUnknownDwellAnalytics({ visible: unknownEvidenceVisible, layoutVersionKnown: Boolean(candidate?.layoutVersionId && candidate.layoutVersionId !== 'unknown') });
  if (!candidate) {
    return (
      <section className="page narrow stack-lg">
        <h1>이 좌석 후보를 찾지 못했어요.</h1>
        <p className="muted">기기에 저장된 후보가 지워졌거나 다른 브라우저에서 연 링크일 수 있어요.</p>
        <Link className="button button-primary" to="/start">좌석 다시 고르기</Link>
      </section>
    );
  }
  const venue = venueById(candidate.venueId);
  if (currentEvidenceStatus !== 'success' || !server) {
    return (
      <section className="page narrow stack-lg" aria-live="polite">
        <Breadcrumb items={[{ label: '홈', href: '/' }, { label: '좌석 확인' }]} />
        <p className="eyebrow">{candidate.venueName} · {candidate.spaceName}</p>
        <SeatPlate section={candidate.sectionCode} row={candidate.rowLabel} number={candidate.seatNumber} />
        <SeatFactsCard candidate={candidate} /><OfficialLayoutStatus candidate={candidate} />
        {memorySection}
        {currentEvidenceStatus === 'loading' ? <p role="status">좌석 근거를 확인하고 있어요.</p> : (
          <div className="card stack">
            <h2>좌석 근거를 아직 확인하지 못했어요.</h2>
            <p>연결이나 응답에 문제가 생겼어요. 기록이 0건이라는 뜻은 아니에요.</p>
            {state.reviews.some((review) => review.candidateId === candidate.candidateId) ? <p>이 기기에 남긴 리뷰는 그대로 있어요.</p> : null}
            <div className="inline">
              <button className="button button-primary" type="button" onClick={() => { setEvidenceStatus('loading'); setEvidenceRetry((value) => value + 1); }}>다시 불러오기</button>
              {(seatLayouts.find((item) => item.venueId === candidate.venueId && item.spaceId === candidate.spaceId)?.source.url ?? venue?.officialSeatmapUrl) ? <ExternalLink className="button" href={seatLayouts.find((item) => item.venueId === candidate.venueId && item.spaceId === candidate.spaceId)?.source.url ?? venue?.officialSeatmapUrl ?? venue?.officialUrl ?? "/support"}>공식 좌석도 열기</ExternalLink> : null}
            </div>
          </div>
        )}
      </section>
    );
  }
  const prices = priceDistribution(reviews.flatMap((review) => review.actualPaid === null ? [] : [review.actualPaid]));
  const evidence = buildCandidateEvidenceSnapshot(server ?? undefined, reviews);
  const fit = buildFitEvidence(state.fitProfile, evidence.fitObservations ?? [], 'nearby_ok');
  const officialLayout = officialLayoutAssessment(candidate);
  const saveSharedCandidate = () => {
    const existing = state.candidates.find((seat) => sameSeat(seat, candidate) && seat.layoutVersionId === candidate.layoutVersionId && seat.instanceId === candidate.instanceId);
    if (existing) { navigate(`/seat/${existing.candidateId}`); return; }
    const saved = { ...candidate, candidateId: createCandidateId() };
    if (!addCandidate(saved)) { setCandidateMessage(state.candidates.length >= 4 ? '후보 네 자리가 이미 있어요. 후보 화면에서 한 자리를 빼면 담을 수 있어요. 개인 기록은 그대로 남아요.' : '기기에 저장하지 못했어요. 위의 복구를 마친 뒤 다시 담아 주세요.'); return; }
    navigate(`/seat/${saved.candidateId}`, { replace: true });
  };
  return (
    <section className="page narrow stack-lg">
      <SurfaceBanner state={surface} empty="이 좌석의 인증 리뷰가 아직 없어요." />
      <Breadcrumb items={[
        { label: '홈', href: '/' },
        { label: candidate.venueName, href: `/venue/${venue?.slug ?? candidate.venueId}` },
        { label: candidate.spaceName },
        { label: `${candidate.sectionCode} ${candidate.rowLabel}열 ${candidate.seatNumber}번` },
      ]} />
      <header className="seat-identity">
        <p className="eyebrow">{candidate.categoryId === 'cinema' ? '영화 좌석' : '공연·콘서트 좌석'}</p>
        <SeatPlate section={candidate.sectionCode} row={candidate.rowLabel} number={candidate.seatNumber} />
        <p className="muted">{candidate.venueName} · {candidate.spaceName}</p>
        <p className="micro">{candidate.instanceLabel ? `선택한 회차 ${candidate.instanceLabel}` : '이 회차의 무대 구성은 아직 확인하지 못했어요.'}</p>
      </header>

      {memorySection}

      {(() => {const layout=seatLayoutFor(candidate.venueId,candidate.spaceId);const path=layout&&venue?rowPath(layout,venue.slug,candidate.sectionCode,candidate.rowLabel):null;return path?<Link className="button" to={path}>이 열 전체 보기</Link>:null;})()}
      <article className="result-card">
        <div>
          <p className="eyebrow">같은 자리·근처·같은 구역</p>
          <div className="evidence-row">
            <EvidenceDistanceBadge tier="exact" count={server.tierCounts.exact} />
            <EvidenceDistanceBadge tier="nearby" count={server.tierCounts.nearby} />
            <EvidenceDistanceBadge tier="section" count={server.tierCounts.section} />
            {server && server.evidence.length === 0
              && server.tierCounts.exact + server.tierCounts.nearby + server.tierCounts.section === 0
              ? <EvidenceDistanceBadge tier="unknown" /> : null}
          </div>
        </div>
        <div className="unknown-state">
          <h2>{server?.evidence.length?`공개 리뷰 ${server.evidence.length}건을 찾았어요.`:'이 좌석은 아직 앉아본 기록이 없어요.'}</h2>
          <p><strong>{officialLayout.changed ? '시설 배치가 달라요.' : '회차 배치는 모름이에요.'}</strong> {officialLayout.statement}</p>
          <div className="inline">
            {candidate.venueId === 'sejong-center' ? <ExternalLink className="button button-primary" href="https://www.sejongpac.or.kr/portal/performance/performance/performList.do?menuNo=200320">공식 공연·회차 찾기</ExternalLink> : null}
            {venue?.officialSeatmapUrl ? <ExternalLink className="button" href={venue.officialSeatmapUrl}>공식 좌석도 열기</ExternalLink> : null}
            {venue?.officialUrl ? <ExternalLink className="button" href={venue.officialUrl}>공식 장소 정보 열기</ExternalLink> : null}
            <ShareSeatButton candidate={candidate} />
          </div>
          <p>{server?.evidence.length?'같은 자리·근처·같은 구역의 기록을 나눠 보여 줘요.':reviews.length?'같은 좌석 기록이지만 공개 전에는 다른 사람의 구매 결정 근거로 쓰지 않아요.':'공식 좌석도에서 위치를 먼저 확인하고, 다녀온 사람의 기록이 생기면 함께 살펴봐요.'}</p>
        </div>
        <div>
          <h3>내 조건</h3>
          <p className="micro">같은 좌석과 가까운 좌석의 공개 기록으로 확인해요. 구역 참고는 체형 표본에 넣지 않아요.</p>
          {[...fit.facts, ...fit.checks].map((statement) => <p className="muted" key={statement}>{statement}</p>)}
          <Link to="/me/fit">내 조건 넣기 →</Link>
        </div>
        <div>
          <h3>실제 낸 돈</h3>
          <p className="muted">{server?.priceDistribution?`공개 표본 ${server.priceDistribution.n}건 · 중앙값 ${server.priceDistribution.median.toLocaleString('ko-KR')}원 (${server.priceDistribution.q1.toLocaleString('ko-KR')}~${server.priceDistribution.q3.toLocaleString('ko-KR')}원)`:prices?`이 기기 기록 ${prices.n}건 · 중앙값 ${prices.median.toLocaleString('ko-KR')}원`:'인증된 실제 결제 기록은 아직 모름이에요.'}</p>
          {candidate.expectedPrice !== null ? <p><strong>{candidate.expectedPrice.toLocaleString('ko-KR')}원</strong> <span className="micro">내가 입력한 예상 금액</span></p> : null}
        </div>
      </article>

      <SeatFactsCard candidate={candidate} /><OfficialLayoutStatus candidate={candidate} />


      {server?.evidence.length?<section className="stack" aria-labelledby="public-reviews"><h2 id="public-reviews">공개 좌석 리뷰</h2>{server.evidence.map((review)=><article className="card" key={String(review.reviewId)}><p className="eyebrow">{{ exact: '같은 자리', nearby: '근처', section: '같은 구역' }[String(review.tier)] ?? '다른 배치'} · {review.verification==='purchase'?'구매 인증':review.verification==='attendance'?'관람 인증':review.source==='anonymous'?'로그인 없이 남긴 한 줄':'미인증 팁'} · {String(review.observedAt).slice(0,10)}</p>{typeof review.photoUrl==='string'?<img src={review.photoUrl} alt="직접 찍은 빈 무대"/>:null}{review.edited?<span>고침</span>:null}{review.note?<p>{String(review.note)}</p>:null}{review.actualPaid?<p className="micro">공개한 실제 결제액 {Number(review.actualPaid).toLocaleString('ko-KR')}원</p>:null}<Link to={`/report/${String(review.reviewId)}`}>이 리뷰 신고하기 →</Link></article>)}</section>:null}

      <ShareSeatImage candidate={candidate} note={typeof server?.evidence[0]?.note==='string'?server.evidence[0].note:''}/><SeatContributions candidate={candidate} onPublished={()=>setEvidenceRetry((v)=>v+1)}/>
      <div className="inline">
        <Link className="button" to="/start">다른 자리 담기</Link>
        {stored
          ? <Link className="button button-primary" to={`/review/new?candidate=${candidate.candidateId}`}>이 좌석 리뷰 남기기</Link>
          : <button className="button button-primary" type="button" onClick={saveSharedCandidate}>내 후보로 담기</button>}
      </div>
      {memories.length && !stored ? <Link className="button" to={`/review/new?candidate=${candidate.candidateId}`}>이 자리 기억 더 남기기</Link> : null}
      {candidateMessage ? <p role="status">{candidateMessage} <Link to="/compare">후보 확인하기</Link></p> : null}
    </section>
  );
}
